import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import apiService from '../services/apiService';

const Career = () => {
  const [searchFilters, setSearchFilters] = useState({
    keywords: '',
    category: 'All Categories',
    jobType: 'All Types',
    location: 'All Locations'
  });
  const [cvForm, setCvForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    cvFile: null
  });
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 5;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalJobs, setTotalJobs] = useState(0);


  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getPublicJobs(currentPage, jobsPerPage);
      
      // Transform API response to match our component structure
      const transformedJobs = response.job_openings.map(job => ({
        id: job.id,
        title: job.title,
        company: job.company,
        location: job.location,
        type: job.type,
        category: 'Engineering', // Default category since API doesn't provide it
        date: job.posted_date,
        description: job.description,
        requirements: job.qualifications || []
      }));
      
      setJobs(transformedJobs);
      setTotalJobs(response.total);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, jobsPerPage]);

  // Fetch jobs on component mount
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleFilterChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCvFormChange = (field, value) => {
    setCvForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf' && file.size <= 10 * 1024 * 1024) {
      setCvForm(prev => ({
        ...prev,
        cvFile: file
      }));
    } else {
      alert('Please upload a PDF file under 10MB');
    }
  };

  const handleCvSubmit = async (e) => {
    e.preventDefault();
    
    if (!cvForm.fullName || !cvForm.email || !cvForm.phone || !cvForm.cvFile) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('full_name', cvForm.fullName);
      formDataToSend.append('email', cvForm.email);
      formDataToSend.append('phone', cvForm.phone);
      formDataToSend.append('files', cvForm.cvFile);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/send/cv`, {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (result.status === 'success') {
        setSubmitSuccess(true);
        setCvForm({
          fullName: '',
          email: '',
          phone: '',
          cvFile: null
        });
        // Reset file input
        const fileInput = document.getElementById('cv-upload');
        if (fileInput) fileInput.value = '';
        
        // Clear success message after 5 seconds
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        setSubmitError(result.message || 'Failed to submit CV');
      }
    } catch (error) {
      setSubmitError('Failed to submit CV. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter logic
  const filteredJobs = jobs.filter(job => {
    const matchesKeywords = !searchFilters.keywords || 
      job.title.toLowerCase().includes(searchFilters.keywords.toLowerCase()) ||
      job.description.toLowerCase().includes(searchFilters.keywords.toLowerCase());
    
    const matchesCategory = searchFilters.category === 'All Categories' || 
      job.category === searchFilters.category;
    
    const matchesJobType = searchFilters.jobType === 'All Types' || 
      job.type === searchFilters.jobType;
    
    const matchesLocation = searchFilters.location === 'All Locations' || 
      job.location.toLowerCase().includes(searchFilters.location.toLowerCase());
    
    return matchesKeywords && matchesCategory && matchesJobType && matchesLocation;
  });

  // Pagination logic
  const totalPages = Math.ceil(totalJobs / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const endIndex = startIndex + jobsPerPage;
  const currentJobs = filteredJobs.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Static data for filters
  const jobCategories = [
    'All Categories',
    'Engineering',
    'Sales & Marketing',
    'Operations',
    'Support',
    'Management'
  ];

  const jobTypes = [
    'All Types',
    'Full Time',
    'Part Time',
    'Contract',
    'Internship'
  ];

  const locations = [
    'All Locations',
    'Remote',
    'New York, NY',
    'San Francisco, CA',
    'New Delhi',
    'Mumbai',
    'Bangalore'
  ];

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when searching
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilters]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Careers at Hospital Build Pvt Ltd | Healthcare Jobs India</title>
        <meta name="description" content="Explore career opportunities at Hospital Build Pvt Ltd. Join our team of experts in Medical Gas Pipeline Systems (MGPS) and hospital infrastructure projects in India." />
        <meta name="keywords" content="Hospital Build Careers, Healthcare Jobs India, MGPS Company Jobs, Hospital Infrastructure Careers, Medical Gas Pipeline Jobs" />
        <link rel="canonical" href="https://www.hospitalbuild.in/career" />
      </Helmet>

      {/* Hero Section - Keep as requested */}
      <section className="relative bg-gradient-to-r from-[#2C2C90] to-[#1A1A5C] text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Join Our Team
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              Build the future of healthcare infrastructure with us. We're looking for passionate individuals who want to make a difference in the medical gas pipeline industry.
            </p>
            {/* <div className="flex flex-wrap justify-center gap-4 text-sm">
              <span className="bg-white/20 px-4 py-2 rounded-full">Innovation</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Growth</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Excellence</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Teamwork</span>
            </div> */}
          </motion.div>
        </div>
      </section>

      {/* Job Search Filters Section */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              {/* Keywords */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Keywords</label>
                <input
                  type="text"
                  placeholder="Search jobs..."
                  value={searchFilters.keywords}
                  onChange={(e) => handleFilterChange('keywords', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={searchFilters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {jobCategories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Job Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Job Type</label>
                <select
                  value={searchFilters.jobType}
                  onChange={(e) => handleFilterChange('jobType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {jobTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <select
                  value={searchFilters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {locations.map((location) => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
          </div>
        </div>

            <button 
              onClick={handleSearch}
              className="bg-blue-800 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search Jobs
            </button>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Job Listings - Left Side */}
            <div className="lg:col-span-2">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{totalJobs} Jobs Found</h2>
                <p className="text-gray-600">Showing {startIndex + 1} to {Math.min(endIndex, totalJobs)} of {totalJobs} positions</p>
              </div>

              {/* Job Cards */}
              <div className="space-y-6">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Loading jobs...</h3>
                    <p className="text-gray-500">Please wait while we fetch the latest job openings.</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading jobs</h3>
                    <p className="text-gray-500 mb-6">{error}</p>
                    <button
                      onClick={fetchJobs}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                ) : currentJobs.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                  </svg>
                </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
                    <p className="text-gray-500">Try adjusting your search criteria or check back later for new opportunities.</p>
                  </div>
                ) : (
                  currentJobs.map((job) => (
                  <div key={job.id} className="bg-white rounded-lg shadow-sm border p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start space-x-4">                        
                <div>
                          <h3 className="text-xl font-semibold text-blue-800 mb-2">
                      {job.title}
                  </h3>
                          <p className="text-gray-600 mb-4">
                            {job.description}
                          </p>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                              </svg>
                              {job.type}
                            </div>
                            <div className="flex items-center gap-1">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                              </svg>
                              {job.location}
                            </div>
                            <div className="flex items-center gap-1">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
                              </svg>
                              {job.date}
                    </div>
                  </div>
                </div>
                </div>
                      <Link
                        to={`/job-details/${job.id}`}
                        className="bg-blue-800 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap inline-block"
                      >
                        Read More →
                      </Link>
          </div>
        </div>
            ))
                )}
              </div>

              {/* Pagination */}
              <div className="flex flex-col items-center space-y-4 mt-8">
                <div className="text-center text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, totalJobs)} of {totalJobs} jobs
        </div>
                
                {totalPages > 1 && (
                  <div className="flex items-center space-x-2">
                    {/* Previous Button */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          currentPage === pageNumber
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                </button>
              ))}

                    {/* Next Button */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                    </div>
                )}
                  </div>
                </div>
                
            {/* CV Upload Form - Right Side */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Upload Your CV</h3>
                  <p className="text-gray-600 text-sm">Quick CV submission for career opportunities</p>
                </div>

                <form onSubmit={handleCvSubmit} className="space-y-4">
                  {/* Success Message */}
                  {submitSuccess && (
                    <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        CV submitted successfully! We'll review your application soon.
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {submitError && (
                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {submitError}
                      </div>
                    </div>
                  )}

                  {/* Full Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      placeholder="Enter your full name"
                      value={cvForm.fullName}
                      onChange={(e) => handleCvFormChange('fullName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={cvForm.email}
                      onChange={(e) => handleCvFormChange('email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      placeholder="Enter your phone number"
                      value={cvForm.phone}
                      onChange={(e) => handleCvFormChange('phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
          </div>

                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Upload CV (PDF) *
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="cv-upload"
                        required
                      />
                      <label htmlFor="cv-upload" className="cursor-pointer">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">Click to upload or drag and drop</p>
                        <p className="text-xs text-gray-500">PDF files only, max 10MB</p>
                      </label>
                    </div>
                    {cvForm.cvFile && (
                      <p className="text-sm text-green-600 mt-2">✓ {cvForm.cvFile.name}</p>
          )}
        </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-800 text-white py-3 rounded-md hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                        Submit CV
                      </>
                    )}
                  </button>
                </form>
              </div>
                </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Career; 